import { EmployeeSchedule, workingPatternToEmployeeSchedule } from '@/helpers/workingPattern/employeeSchedule';
import { format } from 'date-fns';
import { FC } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { TSTitleCard } from '../../../../../components/shared/card/Card';
import { Loader } from '../../../../../components/shared/Loader';
import { dayIndexes } from '../../../../../helpers/dayIndexes';
import {
  ReadEmployee,
  useEmployeeIdAppliedWorkingPatternsForPeriodQuery
} from '../../../../../services/gql/graphql.generated';
import { useScheduleRequestStatus } from '../../../../../services/workingPatterns/useScheduleRequestStatus';
import strings from '../../../team.translations';
import { ScheduleUpdateAlert } from './ScheduleUpdateAlert';

interface TeamMemberAvailabilityScheduleCardProps {
  enableEdit?: boolean;
  employee: ReadEmployee;
}

export const TeamMemberAvailabilityScheduleCard: FC<TeamMemberAvailabilityScheduleCardProps> = ({
  employee,
  enableEdit
}) => {
  const navigate = useNavigate();
  const handleError = useErrorHandler();

  // The backend is a bit weird and won't allow employees with this role to be changed.
  const isManager = employee.role === 'Manager';

  const [{ data, fetching, error }] = useEmployeeIdAppliedWorkingPatternsForPeriodQuery({
    variables: {
      dateAndPeriods: {
        date: format(new Date(), 'yyyy-MM-dd'),
        periods: 7
      },
      id: employee.identityId
    },
    // approve/reject return a plain Guid, so urql's default cache can't tell this query needs
    // invalidating — without this, the card can keep showing pre-approval hours after navigating
    // back from approving/rejecting a request elsewhere (matches the other queries for this data).
    requestPolicy: 'network-only'
  });

  if (error) {
    handleError(error);
  }

  const { pendingRequest, upcomingApprovedRequests, hasPendingRequest } = useScheduleRequestStatus(employee.identityId);

  const dataToRender: EmployeeSchedule[] | undefined = data?.appliedWorkingPatternsByEmployeeId
    ? workingPatternToEmployeeSchedule(data.appliedWorkingPatternsByEmployeeId)
    : undefined;

  const onEditClick = () => {
    navigate(`/team-member/${employee.identityId}/set-schedule`);
  };

  const groupedSchedule = dataToRender?.reduce<Record<number, EmployeeSchedule[]>>((acc, item) => {
    const key = item.dayNumber ?? -1;

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);
    acc[key].sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));

    return acc;
  }, {});

  const orderedSchedule = dayIndexes
    .map(dayNumber => groupedSchedule?.[dayNumber]?.[0])
    .filter(Boolean) as EmployeeSchedule[];

  return (
    <div>
      {hasPendingRequest && pendingRequest?.id && (
        <ScheduleUpdateAlert
          employeeId={employee.identityId}
          requestId={pendingRequest.id}
          message="Pending schedule update. Address it before editing this schedule."
        />
      )}

      {upcomingApprovedRequests.length > 0 && (
        <ScheduleUpdateAlert
          employeeId={employee.identityId}
          message={
            upcomingApprovedRequests.length === 1
              ? `Schedule update approved and effective from ${upcomingApprovedRequests[0].effectiveFrom}.`
              : `${upcomingApprovedRequests.length} schedule updates approved with upcoming effective dates.`
          }
          warning={false}
        />
      )}
      <TSTitleCard
        primaryAction={
          enableEdit && !isManager && !hasPendingRequest
            ? {
                label: strings.scheduleCard.editLabel,
                onClick: onEditClick
              }
            : undefined
        }
        title={strings.scheduleCard.cardTitle}
        icon={AiOutlineCalendar}
      >
        <div className="grid grid-flow-row grid-cols-1 sm:grid-cols-2 sm:grid-flow-col sm:grid-rows-4 gap-y-5 gap-x-8 lg:max-w-6xl lg:mx-auto lg:px-8">
          {fetching ? (
            <Loader className="h-10 mx-auto col-span-full" />
          ) : (
            orderedSchedule.map((item: EmployeeSchedule) => {
              const periods = groupedSchedule?.[item.dayNumber ?? -1]?.filter(period => period.available) ?? [];

              return (
                <div
                  key={item.dayNumber}
                  className="grid grid-cols-2 gap-x-6 items-start"
                  style={{ gridTemplateColumns: '0.8fr 1.2fr' }}
                >
                  <p className="text-right font-semibold sm:px-0">{item.day}</p>
                  <p className="break-keep">
                    {periods.length > 0
                      ? periods.map(period => `${period.start} - ${period.end}`).join(', ')
                      : strings.scheduleCard.notAvailable}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </TSTitleCard>
    </div>
  );
};
